import MoreVertIcon from "@mui/icons-material/MoreVert";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { SxProps } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import classNames from "classnames";
import * as React from "react";
import { Session, useSession } from "../useSession";

export type AccountPreviewVariant = "condensed" | "expanded";

export interface AccountPreviewProps {
  /**
   * The type of account details to display.
   * @property {'condensed'} condensed - Shows only the user's avatar.
   * @property {'expanded'} expanded - Displays the user's avatar, name, and email if available.
   * @default 'condensed'
   */
  variant?: AccountPreviewVariant;
  /**
   * The handler used when the preview is expanded
   */
  handleClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * The state of the Account popover
   * @default false
   */
  open?: boolean;
  /**
   * The prop used to customize the styling of the preview
   */
  sx?: SxProps;
}

function AccountPreview(props: AccountPreviewProps) {
  const { variant = "condensed", open, handleClick, sx } = props;
  const { session } = useSession();

  if (!session || !session.user) {
    return null;
  }

  const getInitials = (session: Session) => {
    const firstInitial = session.user?.firstname
      ? session.user?.firstname.charAt(0).toUpperCase()
      : "";
    const lastInitial = session.user?.lastname
      ? session.user?.lastname.charAt(0).toUpperCase()
      : "";
    return `${firstInitial}${lastInitial}`;
  };

  const avatarContent = (
    <Avatar
      className={classNames("bg-[var(--mui-palette-primary-main)]", {
        "size-8 text-base": variant !== "expanded",
        "size-12 text-xl": variant === "expanded",
      })}
    >
      {getInitials(session)}
    </Avatar>
  );

  if (variant === "expanded") {
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ py: 1, px: 2, gap: 2, ...sx }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          spacing={2}
          overflow="hidden"
        >
          {avatarContent}
          <Stack
            direction="column"
            justifyContent="space-evenly"
            overflow="hidden"
          >
            {session.user?.firstname && session.user?.lastname && (
              <Typography variant="body2" fontWeight="bolder" noWrap>
                {`${session.user?.firstname} ${session.user?.lastname}`}
              </Typography>
            )}
            <Typography variant="caption" noWrap>
              {session.user?.email}
            </Typography>
          </Stack>
        </Stack>
        {handleClick && (
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{ alignSelf: "center" }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    );
  }

  return (
    <Tooltip title={session.user.firstname ?? "Profile"}>
      <Stack sx={{ py: 0.5, ...sx }}>
        <IconButton
          onClick={handleClick}
          aria-label="Profile"
          size="small"
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            width: "fit-content",
            margin: "0 auto",
          }}
        >
          {avatarContent}
        </IconButton>
      </Stack>
    </Tooltip>
  );
}

export { AccountPreview };
